/**
 * View Transitions API Utilities
 * Provides hardware-accelerated transitions for theme changes, navigation, and state updates
 * Falls back gracefully for browsers that don't support the API
 */

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean}
 */
export const supportsViewTransitions = () => {
  return typeof document !== 'undefined' &&
         'startViewTransition' in document;
};

/**
 * Execute a state update with a view transition
 * @param {Function} updateFn - Function that updates the DOM/state
 * @param {Object} options - Transition options
 * @returns {Promise}
 */
export const withViewTransition = async (updateFn, options = {}) => {
  const {
    skipTransition = false,
    fallbackDelay = 0
  } = options;

  // Skip transition if explicitly disabled
  if (skipTransition) {
    updateFn();
    return Promise.resolve();
  }

  // Use View Transitions API if supported
  if (supportsViewTransitions()) {
    const transition = document.startViewTransition(() => {
      updateFn();
    });

    return transition.finished;
  }

  // Fallback for unsupported browsers
  if (fallbackDelay > 0) {
    await new Promise(resolve => setTimeout(resolve, fallbackDelay));
  }
  updateFn();
  return Promise.resolve();
};

/**
 * Theme toggle with view transition
 * @param {Function} toggleFn - Function to toggle theme
 * @param {string} currentTheme - Current theme name
 */
export const transitionTheme = async (toggleFn, currentTheme) => {
  if (!supportsViewTransitions()) {
    toggleFn();
    return;
  }

  // Add specific transition class for theme changes
  document.documentElement.classList.add('theme-transitioning');
  document.documentElement.setAttribute('data-transitioning-from', currentTheme);

  const transition = document.startViewTransition(() => {
    toggleFn();
  });

  try {
    await transition.finished;
  } finally {
    document.documentElement.classList.remove('theme-transitioning');
    document.documentElement.removeAttribute('data-transitioning-from');
  }
};

/**
 * Product navigation with view transition
 * @param {Function} navigationFn - Navigation function
 * @param {string} direction - 'forward' or 'back'
 */
export const transitionNavigation = async (navigationFn, direction = 'forward') => {
  if (!supportsViewTransitions()) {
    navigationFn();
    return;
  }

  document.documentElement.setAttribute('data-navigation-direction', direction);

  const transition = document.startViewTransition(() => {
    navigationFn();
  });

  try {
    await transition.finished;
  } finally {
    document.documentElement.removeAttribute('data-navigation-direction');
  }
};

/**
 * Cart update with view transition
 * @param {Function} updateFn - Function to update cart
 * @param {string} action - 'add' or 'remove'
 */
export const transitionCartUpdate = async (updateFn, action = 'add') => {
  if (!supportsViewTransitions()) {
    updateFn();
    return;
  }

  document.documentElement.setAttribute('data-cart-action', action);

  const transition = document.startViewTransition(() => {
    updateFn();
  });

  try {
    await transition.finished;
  } finally {
    document.documentElement.removeAttribute('data-cart-action');
  }
};

/**
 * Generic state update with view transition
 * @param {Function} updateFn - State update function
 * @param {string} transitionName - Name for the transition
 */
export const transitionStateUpdate = async (updateFn, transitionName = 'state-update') => {
  return withViewTransition(updateFn, {
    skipTransition: false
  });
};
